import {importRemoteBlocks} from '@packages/shared/utils/importRemoteBlocks';
import React from 'react';
import {List, fromJS} from 'immutable';
import {QualifiedMap} from '@packages/systems/wfdl-Qualified';
import {importBreakpoints} from '@packages/systems/style/media-queries';
import {
  VariableMap,
  variableCollectionMapFromArray,
} from '@packages/systems/core/utils/StyleVariables';
import {getRootExpression} from '../../../utils/getRootExpression';
import {allowedDataTypesToReceive} from '../../constants';
import {
  EventDataType,
  ProjectDataInEventDataType,
  type ProjectDataToStore,
} from '../../types';
import PageRecord from '@packages/systems/core/records/PageRecord';
import {AssetState} from '@packages/systems/designer/assets';
import {indexById} from '@packages/systems/core/utils/RecordUtils';
import {reifyFile} from '@packages/shared/utils/AssetUtils';
import {
  type IXState as IXStateType,
  IXState,
  importRemoteMacros,
} from '@packages/systems/designer/ix';
import StyleBlockState from '@packages/shared/LegacyStyleSystem/states/StyleBlockState';
import {RawStyleBlockServerType} from '@packages/systems/core/utils/StyleTypes';
import {FileRecord} from '@packages/systems/core/records/FileRecord';
import {type RemoteCustomFont} from '@packages/systems/designer/site-data';
import {
  getDesignerAppOrigin,
  getDesignerPreviewOrigin,
  getAnonymousCommentsAppOrigin,
} from '@packages/utilities/canvas-origin';
import {reifyCollections} from '@packages/systems/dynamo/utils/CollectionUtils';
import {generateVariantDataMap} from '@packages/systems/component-style-variants';
import {
  EMPTY_INSTALLED_LIBRARY_TAG_STYLES_SETTINGS,
  LibrariesCapabilitiesMap,
} from '@packages/systems/libraries';
import {handleFunctionExecutionRequested} from './handleFunctionExecutionRequested';
import {CodeIslandCustomElementStore} from '@packages/systems/plugins/Code';
import {designerDataMessageBuffer} from './DesignerDataMessageBuffer';

/** Hook to receive any needed data for Canvas App functioning from Designer App  */
export const useReceivedProjectData = () => {
  const [data, setData] = React.useState<Partial<ProjectDataToStore>>({});
  // Allow identify allowed origins from which Canvas App is allowed to receive messages
  // In our case it is only Designer App in which it is embedded and Designer App in Read-Only mode (for example, preview.webflow.com)
  const designerAppOrigin = getDesignerAppOrigin(window.location.origin);
  const designerPreviewOrigin = getDesignerPreviewOrigin(
    window.location.origin
  );
  const anonymousCommentsAppOrigin = getAnonymousCommentsAppOrigin(
    window.location.origin
  );

  React.useEffect(() => {
    const receiveMessage = (event: MessageEvent<EventDataType>) => {
      if (event.source !== window.parent) {
        return;
      }

      // Allow identify allowed origins from which Canvas App is allowed to receive messages
      // In our case it is only Designer App in which it is embedded and Designer App in Read-Only mode (for example, preview.webflow.com)
      if (
        (designerAppOrigin && event.origin === designerAppOrigin) ||
        (designerPreviewOrigin && event.origin === designerPreviewOrigin) ||
        (anonymousCommentsAppOrigin &&
          event.origin === anonymousCommentsAppOrigin)
      ) {
        const {type} = event.data;

        if (type === 'function') {
          if (
            event.origin === designerAppOrigin ||
            event.origin === anonymousCommentsAppOrigin
          ) {
            handleFunctionExecutionRequested(event.data, event.origin);
          }
          return;
        }

        if (type === 'INJECT_TAG_CSS') {
          // Handle CSS injection messages separately - these are not project data
          // The CSS injection is handled by the iframe's message listeners
          return;
        }

        if (type && allowedDataTypesToReceive.has(type)) {
          const projectData = event.data as ProjectDataInEventDataType;
          setData((prevData) => updateProjectData(prevData, projectData));
        }
      }
    };

    window.addEventListener('message', receiveMessage);
    // Replay anything the Designer posted before this effect ran (see
    // DesignerDataMessageBuffer). The live listener is attached first so nothing can
    // slip between the two.
    designerDataMessageBuffer.drain(receiveMessage);

    return () => {
      window.removeEventListener('message', receiveMessage);
    };
  }, [designerAppOrigin, designerPreviewOrigin, anonymousCommentsAppOrigin]);

  return data;
};

/** Based on type of the data received from Designer App apply some transformation and store in the Canvas app state */
const updateProjectData = (
  prevData: Partial<ProjectDataToStore>,
  eventData: ProjectDataInEventDataType
): Partial<ProjectDataToStore> => {
  const type = eventData.type;

  if (type === 'components') {
    const components = new QualifiedMap(eventData.data._data);

    if (!components) {
      return prevData;
    }

    return {
      ...prevData,
      components,
      rootExpression: getRootExpression(true)(components),
      variantDataMap: generateVariantDataMap(components),
    };
  }

  if (type === 'currentPage') {
    const currentPageRecord = PageRecord(eventData.data);

    return {
      ...prevData,
      currentPage: currentPageRecord,
      pageId: currentPageRecord.id,
    };
  }

  if (type === 'breakpoints') {
    const styleBlockState = prevData.styleBlockState || new StyleBlockState();
    const breakpoints = importBreakpoints(eventData.data);

    return {
      ...prevData,
      styleBlockState: styleBlockState.set('breakpoints', breakpoints),
    };
  }

  if (type === 'assets') {
    const assets = eventData.data;

    return {
      ...prevData,
      assetState: new AssetState().mergeIn(
        ['assets'],
        indexById(List(assets).map(reifyFile))
      ),
    };
  }

  if (type === 'customFonts') {
    const customFontsData = eventData.data;

    return {
      ...prevData,
      customFonts: customFontsData.map((customFontData: RemoteCustomFont) => ({
        ...customFontData,
        file: new FileRecord(customFontData.file),
      })),
    };
  }

  if (type === 'cssVariablesState') {
    const cssVariablesStateData = eventData.data;
    return {
      ...prevData,
      cssVariablesState: {
        ...cssVariablesStateData,
        variables: VariableMap.from(cssVariablesStateData.variables),
        variableCollections: variableCollectionMapFromArray(
          cssVariablesStateData.variableCollections
        ),
      },
    };
  }

  if (type === 'ixStateRawMacros') {
    const ixStateRawMacros = eventData.data as IXStateType['rawMacros'];

    return {
      ...prevData,
      ixState: importRemoteMacros(new IXState(), {rawMacros: ixStateRawMacros}),
    };
  }

  if (type === 'collections') {
    const collectionsData = eventData.data;
    return {
      ...prevData,
      collections: reifyCollections(collectionsData),
    };
  }

  if (type === 'items') {
    const itemsPlain = eventData.data;
    return {
      ...prevData,
      items: fromJS(itemsPlain),
    };
  }

  if (type === 'jsonStyles') {
    const styleBlockState = prevData.styleBlockState || new StyleBlockState();
    const rawBlocks = eventData.data as RawStyleBlockServerType[];

    const updatedStyleBlockState = importRemoteBlocks(styleBlockState, {
      rawBlocks,
    });

    return {
      ...prevData,
      styleBlockState: updatedStyleBlockState.set(
        'installedLibrariesTagStylesSettings',
        prevData.installedLibrariesTagStylesSettings ??
          EMPTY_INSTALLED_LIBRARY_TAG_STYLES_SETTINGS
      ),
    };
  }

  if (type === 'currentItem') {
    const currentItemPlain = eventData.data;
    return {
      ...prevData,
      currentItem: fromJS(currentItemPlain),
    };
  }

  if (type === 'ecommShippingZones') {
    const ecommShippingZonesPlain = eventData.data;
    return {
      ...prevData,
      ecommShippingZones: fromJS(ecommShippingZonesPlain),
    };
  }

  if (type === 'ecommBusinessAddress') {
    const ecommBusinessAddressPlain = eventData.data;
    return {
      ...prevData,
      ecommBusinessAddress: fromJS(ecommBusinessAddressPlain),
    };
  }

  if (type === 'variableCollections') {
    const variableCollections = eventData.data;
    return {
      ...prevData,
      variableCollections: variableCollectionMapFromArray(variableCollections),
    };
  }

  if (type === 'librariesCapabilitiesMap') {
    const librariesCapabilitiesMap = eventData.data;
    return {
      ...prevData,
      librariesCapabilitiesMap: Object.entries(librariesCapabilitiesMap).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            isHidden: value.isHidden,
            variables: new Set(value.variables),
            styleBlocks: new Set(value.styleBlocks),
          };
          return acc;
        },
        {} as LibrariesCapabilitiesMap
      ),
    };
  }

  if (type === 'codeIslandStore') {
    const codeIslandStoreData = eventData.data;

    let store = CodeIslandCustomElementStore.getInstance();

    if (!store) {
      // Initialize the store if it doesn't exist
      store = CodeIslandCustomElementStore.init();
    }

    // Update the store state with the received data
    store.state = codeIslandStoreData.state;

    // Store is attached to window, not React state, so we don't need to return it
    return prevData;
  }

  if (type === 'installedLibrariesTagStylesSettings') {
    const styleBlockState = prevData.styleBlockState || new StyleBlockState();
    const installedLibrariesTagStylesSettings = eventData.data;

    return {
      ...prevData,
      installedLibrariesTagStylesSettings,
      styleBlockState: styleBlockState.set(
        'installedLibrariesTagStylesSettings',
        installedLibrariesTagStylesSettings
      ),
    };
  }

  if (type === 'componentLocalizations') {
    return {
      ...prevData,
      componentLocalizations: new QualifiedMap(eventData.data._data),
    };
  }

  return {
    ...prevData,
    [type]: eventData.data as ProjectDataToStore[typeof type],
  };
};
